'use client';

import { useEffect, useState, useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import api from '@/utils/api';
import { useIdleTimer } from '@/hooks/useIdleTimer';

const PUBLIC_ROUTES = ['/login'];

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const isPublic = PUBLIC_ROUTES.includes(pathname);
  const [isChecking, setIsChecking] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const handleIdleLogout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggedIn(false);
      router.push('/login');
      router.refresh();
    }
  }, [router]);

  useIdleTimer(handleIdleLogout, isLoggedIn && !isPublic);

  useEffect(() => {
    let isMounted = true;

    async function checkSession() {
      try {
        const response = await api.get('/auth/session', {
          validateStatus: (status) => status === 200 || status === 401,
        });

        if (isMounted) {
          setIsLoggedIn(response.status === 200);
        }
      } catch {
        if (isMounted) {
          setIsLoggedIn(false);
        }
      } finally {
        if (isMounted) {
          setIsChecking(false);
        }
      }
    }

    checkSession();

    return () => {
      isMounted = false;
    };
  }, [pathname]);

  useEffect(() => {
    if (isChecking) return;

    if (!isLoggedIn && !isPublic) {
      router.replace('/login');
    } else if (isLoggedIn && isPublic) {
      router.replace('/');
    }
  }, [isChecking, isLoggedIn, isPublic, pathname, router]);

  if (isChecking) return null;
  if (!isLoggedIn && !isPublic) return null;
  if (isLoggedIn && isPublic) return null;

  return <>{children}</>;
}
